import React, { useEffect, useRef } from 'react'

// Physical USB/Bluetooth QR & barcode scanners act as "HID keyboard wedge"
// devices: no browser scanning API involved, they simply type the decoded
// code into whatever input is currently focused, then send Enter. This
// component keeps one input focused for as long as a clock-in/clock-out mode
// is active and turns each finished scan into one onScan(code) call — it does
// not stop listening after a single scan, so consecutive employees never need
// the mode button pressed again between them.
const SCAN_QUIET_PERIOD_MS = 150

const KioskHardwareScanInput = ({ active, onScan }) => {
    const inputRef = useRef(null)
    const quietTimer = useRef(null)

    useEffect(() => {
        if (!active) {
            return undefined
        }

        inputRef.current?.focus()

        // A tap anywhere on the kiosk screen (including the mode buttons
        // themselves) can steal focus from this hidden input; grab it back so
        // the physical scanner keeps working without staff intervention.
        const refocus = () => inputRef.current?.focus()
        document.addEventListener('click', refocus)

        return () => {
            document.removeEventListener('click', refocus)
            clearTimeout(quietTimer.current)
        }
    }, [active])

    if (!active) {
        return null
    }

    const finalize = () => {
        clearTimeout(quietTimer.current)
        const code = inputRef.current?.value.trim()
        if (inputRef.current) {
            inputRef.current.value = ''
        }
        if (code) {
            onScan(code)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            finalize()
        }
    }

    const handleChange = () => {
        // Fallback for a scanner configured without a trailing Enter: finalize
        // once keystrokes stop arriving for a short quiet period.
        clearTimeout(quietTimer.current)
        quietTimer.current = setTimeout(finalize, SCAN_QUIET_PERIOD_MS)
    }

    return (
        <input
            ref={inputRef}
            type="text"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            inputMode="none"
            autoComplete="off"
            aria-hidden="true"
            className="absolute opacity-0 w-px h-px"
        />
    )
}

export default KioskHardwareScanInput
